import React from 'react';
import { BarChart, Trash2, Layers, MapPin, Star } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для отображения популярных маршрутов и сохраненных полетов
 */
const PopularRoutesSidebar = ({ 
  savedFlights, 
  deleteSavedFlight, 
  loadSavedFlight 
}) => {
  // Популярные маршруты в России
  const popularRoutes = [
    { 
      from: 'Москва', 
      to: 'Санкт-Петербург', 
      distance: 650, 
      time: '1ч 20м', 
      description: 'Самый популярный внутренний маршрут России' 
    },
    { 
      from: 'Москва', 
      to: 'Сочи', 
      distance: 1360, 
      time: '2ч 30м', 
      description: 'Популярное курортное направление' 
    },
    { 
      from: 'Москва', 
      to: 'Екатеринбург', 
      distance: 1420, 
      time: '2ч 20м', 
      description: 'Связь столицы с Уралом' 
    },
    { 
      from: 'Москва', 
      to: 'Казань', 
      distance: 720, 
      time: '1ч 35м', 
      description: 'Важный культурный и экономический центр' 
    },
    { 
      from: 'Санкт-Петербург', 
      to: 'Калининград', 
      distance: 950, 
      time: '2ч', 
      description: 'Связь с западным эксклавом России' 
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      {/* Заголовок раздела с сохраненными маршрутами */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-blue-500" />
          {savedFlights && savedFlights.length > 0 
            ? 'Сохраненные маршруты' 
            : 'Популярные маршруты'}
        </h3>
        {savedFlights && savedFlights.length > 0 && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {savedFlights.length} {savedFlights.length === 1 ? 'маршрут' : 
             savedFlights.length < 5 ? 'маршрута' : 'маршрутов'}
          </span>
        )}
      </div>

      {/* Раздел сохраненных маршрутов */}
      {savedFlights && savedFlights.length > 0 ? (
        <div className="space-y-4">
          {savedFlights.map((flight) => (
            <div 
              key={flight.id} 
              className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {flight.name}
                </h4>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Layers className="w-4 h-4" />}
                    onClick={() => loadSavedFlight(flight.id)}
                    title="Загрузить маршрут"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 className="w-4 h-4 text-red-500" />}
                    onClick={() => deleteSavedFlight(flight.id)}
                    title="Удалить"
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between mb-1">
                  <span>Маршрут:</span>
                  <span className="font-medium">
                    {flight.formData.departureCity} → {flight.formData.arrivalCity}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Тип самолета:</span>
                  <span>
                    {flight.formData.aircraftType === 'regional' ? 'Региональный' : 
                     flight.formData.aircraftType === 'small' ? 'Малый' : 
                     flight.formData.aircraftType === 'medium' ? 'Средний' : 'Дальнемагистральный'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Расстояние:</span>
                  <span className="font-medium text-blue-600 dark:text-blue-400">
                    {formatNumber(flight.results.totalDistance, 0)} км
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Раздел популярных маршрутов (отображается, если нет сохраненных)
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Самые популярные авиамаршруты России, которые ежедневно соединяют крупнейшие города страны:
          </p>
          
          {popularRoutes.map((route, index) => (
            <div 
              key={index} 
              className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-amber-500" />
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {route.from} – {route.to}
                </h4>
              </div>
              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                <span className="flex items-center">
                  <MapPin className="w-3 h-3 mr-1 text-blue-500" />
                  {formatNumber(route.distance, 0)} км
                </span>
                <span className="flex items-center">
                  <Layers className="w-3 h-3 mr-1 text-blue-500" />
                  ~{route.time}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {route.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Дополнительная информация о маршрутах */}
      {!savedFlights || savedFlights.length === 0 ? (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <MapPin className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
                Внутренняя авиация России
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                Авиационная сеть России связывает более 250 аэропортов по всей стране, 
                обеспечивая транспортную доступность даже самых удаленных регионов.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PopularRoutesSidebar;